import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import SpeciesSearchWidget from './SpeciesSearchWidget';
import SpeciesGallery from './SpeciesGallery';
import SpeciesDescriptionsBox from './SpeciesDescriptionsBox';
import SpeciesKeysBox from './SpeciesKeysBox';
import SpeciesSynonymsBox from './SpeciesSynonymsBox';
import SpeciesChildrenBox from './SpeciesChildrenBox';
import { speciesHeader, speciesReferencesBox, speciesClassificationBox } from './SpeciesInfoBoxes';
import wantsCol from '../utils/wantsCol';
/**
 * /species/:id - shows a GBIF species (enriched with champis.net's local
 * corrections, see SpeciesResource) alongside every discussion tagged with
 * it (via the migrated champis_species_topic_species snapshot + the
 * `species` search filter, see SpeciesFilter).
 *
 * Also the base class for TaxonPage (/taxon/:id, see that file's own
 * docblock) - the Catalogue of Life-flavored counterpart, same page in
 * every other respect (GbifSpeciesRecord.id is always the gbif_id
 * regardless of which route fetched it, see TaxonResource's own docblock,
 * so SpeciesGallery/SpeciesKeysBox/SpeciesChildrenBox/SpeciesSynonymsBox/
 * the discussions list below all work unchanged either way). `source`
 * picks which endpoint to call; `sourceParam()` picks the URL segment
 * that identifies the page for humans/logs (kept out of `source` itself,
 * which is also used as the API path segment).
 */
export default class SpeciesPage extends Page {
  species = null;
  speciesError = false;
  discussions = null;

  // Only ever set on the /taxon/:id route, for a COL id with no gbif_id at
  // all (see TaxonResource's own docblock) - a genus/family/order/... COL
  // id essentially never has a local mapping, since col_id is only ever
  // recorded at species level. Reuses GbifSpeciesRecord's own shape rather
  // than a new type: TaxonResource's response really does share it (same
  // canonicalName/rank/kingdom/kingdomColId/... fields), just always null
  // for every GBIF-only field (gbifId, *Key, status, synonym*, ...) - see
  // TaxonPage's own view() override for the much thinner layout this
  // renders instead of the rest of this page's own gbif_id-keyed sections.
  colTaxon = null;
  source = 'species';
  oninit(vnode) {
    super.oninit(vnode);
    this.species = null;
    this.speciesError = false;
    this.loadSpecies(m.route.param('id'));
  }
  loadSpecies(id) {
    this.species = null;
    this.colTaxon = null;
    this.speciesError = false;
    this.discussions = null;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/' + this.source + '/' + id + '?includeReferences=true&vernacularName=true'
    }).then(response => {
      // Only ever possible on /taxon/:id (SpeciesResource's own response
      // never sends gbifBacked at all) - a COL id with no gbif_id at all,
      // see this class's own colTaxon docblock. Checked before touching
      // response.data.id at all: for this case it's the COL id itself
      // (e.g. "T7Z"), not a gbif_id - Number(...) on it would be NaN.
      if (this.source === 'taxon' && response.data.attributes.gbifBacked === false) {
        this.colTaxon = {
          id: response.data.id,
          ...response.data.attributes
        };
        app.setTitle(this.colTaxon.canonicalName);
        m.redraw();
        return;
      }
      this.species = {
        id: Number(response.data.id),
        ...response.data.attributes
      };

      // Only ever redirects away from the plain GBIF route, and only
      // once both conditions genuinely favor Catalogue of Life: the
      // actor's own saved preference (see addDataSourceSetting.tsx) and
      // this exact species actually having a resolved col_id (`colId`,
      // only ever populated on SpeciesResource's own response - see its
      // docblock). Replaces history rather than pushing, so the
      // intermediate /species/:id URL a visitor never meant to land on
      // doesn't leave a back-button entry of its own. TaxonPage itself
      // never re-enters this branch (its own `source` is already
      // 'taxon'), so there's no risk of a redirect loop.
      if (this.source === 'species' && this.species.colId && wantsCol()) {
        m.route.set(app.route.taxon(this.species.colId, this.species.canonicalName), undefined, {
          replace: true
        });
        return;
      }

      // No col_id resolved for this species at all yet, in COL mode -
      // SpeciesResource itself already shows this "as if no species were
      // linked" (a genuinely blank canonicalName, see
      // SpeciesLookupService::buildColSpecies()'s own docblock), but this
      // page used to still render its own shell around that blank data -
      // a header with an empty title and a "Référentiels" box whose GBIF
      // link still worked, showing a real GBIF page for a species this
      // mode shouldn't display at all (confirmed live 2026-09-21). Same
      // "not found" treatment as a genuinely bad id, rather than a new,
      // separate empty state - this route was never meant to be reached
      // this way in the first place (see the redirect right above, and
      // TaxonPage.tsx's own docblock on how a visitor normally gets to a
      // COL-mode species page at all).
      if (this.source === 'species' && wantsCol() && !this.species.colId && !this.species.canonicalName) {
        this.speciesError = true;
        m.redraw();
        return;
      }
      this.setTitle();

      // response.data.id is the *accepted* species' id even when `id`
      // (from the URL) was a synonym - the discussion filter expands
      // this server-side to include every synonym's discussions too
      // (see SpeciesFilter), so this is the one id it needs.
      // A COL-mode page (/taxon/:id) groups by COL's own synonymy
      // (TaxonFilter), a GBIF one by GBIF's (SpeciesFilter) - see
      // listColId().
      const colId = this.listColId();
      this.discussions = new DiscussionListState({
        filter: colId ? {
          taxon: colId
        } : {
          species: this.species.id
        }
      });
      this.discussions.refresh();
      m.redraw();
    }).catch(() => {
      this.speciesError = true;
      m.redraw();
    });
  }
  setTitle() {
    if (this.species) {
      // Same primary-identity swap as speciesHeader() - a synonym's own
      // name, not the accepted species it redirected to (see
      // SpeciesInfoBoxes.js's docblock for why synonymScientificName being
      // set at all is the signal for this).
      app.setTitle(this.species.synonymScientificName || this.species.scientificName);
    }
  }
  view() {
    return m("div", {
      className: "Page SpeciesPage"
    }, m("div", {
      className: "container"
    }, this.speciesError ? m("p", {
      className: "SpeciesPage-error"
    }, app.translator.trans('champis-species.forum.species_page.not_found')) : !this.species ? m(LoadingIndicator, null) : [speciesHeader(this.species), m(SpeciesGallery, {
      gbifId: this.species.id,
      colId: this.listColId()
    })], this.species && m("div", {
      className: "Page-container"
    }, m("div", {
      className: "Page-content"
    }, m(SpeciesDescriptionsBox, {
      gbifId: this.species.id,
      colId: this.listColId()
    }), this.discussions && m("div", {
      className: "SpeciesPage-discussions"
    }, m("h2", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-comments",
      "aria-hidden": "true"
    }), ' ', this.discussions.totalItems === null ? app.translator.trans('champis-species.forum.species_page.discussions_header') : app.translator.trans('champis-species.forum.species_page.discussions_count', {
      count: this.discussions.totalItems
    })), m("div", {
      className: "IndexPage-toolbar"
    }, m("ul", {
      className: "IndexPage-toolbar-view"
    }, m("li", {
      className: "item-sort"
    }, this.sortItem()))), m(DiscussionList, {
      state: this.discussions
    })), m(SpeciesKeysBox, {
      gbifId: this.species.id,
      colId: this.listColId()
    })), m("div", {
      className: "Page-sidebar"
    }, m(SpeciesSearchWidget, null), speciesReferencesBox(this.species), speciesClassificationBox(this.species), m(SpeciesChildrenBox, {
      gbifId: this.species.id
    }), m(SpeciesSynonymsBox, {
      gbifId: this.species.id
    })))));
  }

  // Mirrors IndexPage's own 'sort' toolbar item (core/forum/components/IndexPage.tsx),
  // but bound to this page's own filtered `this.discussions` state instead of
  // the global `app.discussions` - sorting here shouldn't affect the site-wide
  // discussion list. Rendered inside .IndexPage-toolbar so it picks up the
  // exact same "merged into the blue bar" styling as the homepage/tag pages.
  /**
   * On a COL-mode page (/taxon/:id), the accepted taxon's COL id every list
   * below groups by - COL's own synonymy instead of GBIF's (see
   * ColTaxonGroupResolver) - so a discussion tagged with a COL synonym shows
   * up here, like GBIF mode does with GBIF's synonyms. Null on a GBIF page.
   */
  listColId() {
    if (this.source !== 'taxon' || !this.species) return null;
    return this.species.synonymAcceptedColId ?? this.species.colId ?? null;
  }
  sortItem() {
    const discussions = this.discussions;
    const sortMap = discussions.sortMap();
    const sortOptions = Object.keys(sortMap).reduce((acc, sortId) => {
      const sort = sortMap[sortId];
      acc[sortId] = typeof sort !== 'string' ? sort.label : app.translator.trans(`core.forum.index_sort.${sortId}_button`);
      return acc;
    }, {});
    const current = discussions.currentSort ? discussions.currentSort() : null;
    return m(Dropdown, {
      buttonClassName: "Button",
      label: sortOptions[current] || Object.keys(sortOptions).map(key => sortOptions[key])[0],
      accessibleToggleLabel: app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label')
    }, Object.keys(sortOptions).map(value => {
      const label = sortOptions[value];
      const active = (current || Object.keys(sortMap)[0]) === value;
      return m(Button, {
        icon: active ? 'fas fa-check' : true,
        onclick: () => discussions.changeSort(value),
        active: active
      }, label);
    }));
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesPage', SpeciesPage);